import React, { useRef } from 'react';
import { Button, Input, TreeSelect } from '@myua/kit';
import { DroppedData, flatten, isNotOSFile, useDropzone } from '@myua/dropzone';
import classNames from 'classnames';
import { UploadTarget } from '../useUploadTarget';

export const UploadIconLarge = () => (
  <svg width="42" height="42" viewBox="0 0 42 42" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      opacity="0.4"
      d="M29.4 15.75H12.6C7 15.75 3.5 19.25 3.5 24.85V29.3825C3.5 35 7 38.5 12.6 38.5H29.3825C34.9825 38.5 38.4825 35 38.4825 29.4V24.85C38.5 19.25 35 15.75 29.4 15.75Z"
      fill="#1862D1"
    />
    <path
      d="M27.79 9.74703L21.9275 3.88453C21.42 3.37703 20.58 3.37703 20.0725 3.88453L14.21 9.74703C13.7025 10.2545 13.7025 11.0945 14.21 11.602C14.7175 12.1095 15.5575 12.1095 16.065 11.602L19.6875 7.97953V26.687C19.6875 27.4045 20.2825 27.9995 21 27.9995C21.7175 27.9995 22.3125 27.4045 22.3125 26.687V7.97953L25.935 11.602C26.1975 11.8645 26.53 11.987 26.8625 11.987C27.195 11.987 27.5275 11.8645 27.79 11.602C28.315 11.0945 28.315 10.272 27.79 9.74703Z"
      fill="#1862D1"
    />
  </svg>
);

// "Project → folder" row: an existing folder or "New folder" with its name input.
export const UploadDestination: React.FC<{
  target: UploadTarget;
  disabled?: boolean;
  autoFocus?: boolean;
}> = ({ target, disabled = false, autoFocus = false }) => {
  if (target.projectName === null) return null;
  return (
    <div className="al-upload-modal__destination">
      <span className="al-upload-modal__destination-item">{target.projectName}</span>
      <span className="al-upload-modal__destination-arrow">→</span>
      <TreeSelect
        className="al-upload-modal__destination-select"
        treeData={target.destinationTreeData}
        fieldNames={{ value: 'key', label: 'title' }}
        value={target.selectValue}
        placeholder="Select folder"
        treeDefaultExpandAll
        // useUploader.upload fixes the target folder before font detection /
        // license confirmation (pendingUpload), so a change then would be ignored.
        disabled={disabled}
        onChange={target.selectDestination}
      />
      {target.newFolderActive && (
        <Input
          className="al-upload-modal__new-folder"
          autoFocus={autoFocus}
          placeholder="Enter folder name"
          value={target.newFolderName}
          maxLength={255}
          status={target.folderNameTaken ? 'error' : undefined}
          disabled={disabled}
          onChange={(e) => target.setNewFolderName(e.target.value)}
        />
      )}
      {target.newFolderActive && target.folderNameTaken && (
        <div className="al-upload-modal__new-folder-error">
          A folder with this name already exists — pick it from the list
        </div>
      )}
    </div>
  );
};

export const UploadDropzone: React.FC<{
  target: UploadTarget;
  disabled?: boolean;
  onFiles: (files: File[]) => void;
  className?: string;
}> = ({ target, disabled = false, onFiles, className }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const blocked = target.uploadBlocked;

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    onFiles(files);
  };

  const onDrop = (data: DroppedData) => {
    const flat = flatten(data);
    onFiles((flat[''] ?? []).filter(isNotOSFile));
  };

  const { getRootProps, isDragActive } = useDropzone<HTMLDivElement>({
    handler: onDrop,
    disabled: disabled || blocked,
    ref: dropzoneRef,
  });

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={onPick}
      />
      <div
        ref={dropzoneRef}
        className={classNames('al-upload-modal__dropzone', className, {
          'al-upload-modal__dropzone_active': isDragActive && !blocked,
          'al-upload-modal__dropzone_disabled': blocked,
        })}
        {...getRootProps()}
      >
        <div className="al-upload-modal__dropzone-icon">
          <UploadIconLarge />
        </div>
        <div className="al-upload-modal__dropzone-title">
          {blocked ? target.blockedText : 'Browse files or drag and drop here'}
        </div>
        <Button
          type="primary"
          className="al-upload-modal__choose-button"
          disabled={blocked}
          onClick={() => fileInputRef.current?.click()}
        >
          Choose files
        </Button>
      </div>
    </>
  );
};
